import { useCallback, useEffect, useRef, useState } from 'react';
import { protocolPdfsApi, ProtocolPdfsStatus, ProtocolPackInclude } from '../services/api';

// E5 (§8, 2026-08-04): polling joba hurtowego pobierania PDF-ów protokołów z CEEB.
// Wzorzec 1:1 z useCeebImportPolling (kanon pollingu — ZAKAZ inline setInterval w
// komponentach). Anty-stale jest tu prostszy niż w imporcie: jobId jest UNIKALNY per
// run (świeży status-file), więc wystarczy pilnować bieżącego jobId.
export type ProtocolPdfsUiStatus = 'idle' | 'running' | 'done' | 'error';

export interface UseProtocolPdfsPolling {
  status: ProtocolPdfsUiStatus;
  result: ProtocolPdfsStatus | null;
  jobId: string | null;
  /**
   * E6: `protocolYear` opcjonalny — pominięty = wszystkie lata z historii. #416: `include` = komplet.
   * `opts.heatSources` (Bronek 14.10): podsumowanie w komplecie ze „Źródłami ciepła".
   */
  start: (
    buildingIds: string[], protocolYear?: number, include?: ProtocolPackInclude[], opts?: { heatSources?: boolean },
  ) => Promise<void>;
}

const POLL_INTERVAL_MS = 3000;
// Duże paczki (limit 100 budynków × historia lat) potrafią schodzić minutami — twardy
// timeout 10 min chroni przed zawisłym subprocesem, nie ścigając normalnych przebiegów.
const MAX_POLL_MS = 600000;

export function useProtocolPdfsPolling(): UseProtocolPdfsPolling {
  const [status, setStatus] = useState<ProtocolPdfsUiStatus>('idle');
  const [result, setResult] = useState<ProtocolPdfsStatus | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);

  const pollRef = useRef<number | null>(null);
  const startedAtRef = useRef<number>(0);
  const jobRef = useRef<string | null>(null);

  const clearPoll = useCallback(() => {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => clearPoll, [clearPoll]);

  const start = useCallback(async (
    buildingIds: string[], protocolYear?: number, include?: ProtocolPackInclude[], opts?: { heatSources?: boolean },
  ) => {
    clearPoll();
    setStatus('running');
    setResult(null);
    try {
      // Opcję przekazujemy tylko włączoną — domyślne żądanie bez zmian.
      const { jobId: id } = opts?.heatSources
        ? await protocolPdfsApi.start(buildingIds, protocolYear, include, { heatSources: true })
        : await protocolPdfsApi.start(buildingIds, protocolYear, include);
      jobRef.current = id;
      setJobId(id);
      startedAtRef.current = Date.now();
      pollRef.current = window.setInterval(async () => {
        // Guard anty-stale: reaguj wyłącznie na status WŁASNEGO joba (start mógł ruszyć nowy).
        const current = jobRef.current;
        if (current !== id) { return; }
        if (Date.now() - startedAtRef.current > MAX_POLL_MS) {
          clearPoll();
          setStatus('error');
          setResult({ status: 'error', error: 'Przekroczono czas oczekiwania na paczkę' });
          return;
        }
        try {
          const s = await protocolPdfsApi.status(id);
          setResult(s);
          if (s.status === 'done' || s.status === 'error') {
            clearPoll();
            setStatus(s.status);
          }
        } catch (_) {
          // pojedynczy nieudany poll ignorujemy (chwilowy brak pliku/sieci) — timeout łapie resztę
        }
      }, POLL_INTERVAL_MS);
    } catch (e) {
      clearPoll();
      setStatus('error');
      setResult({ status: 'error', error: e instanceof Error ? e.message : 'Nie udało się wystartować' });
    }
  }, [clearPoll]);

  return { status, result, jobId, start };
}
